/**
 * "vessels is an identifier lookup, not a text search" (overview §1.2), so this
 * is where the client enforces the rule BEFORE a credit is at risk: a name
 * typed into the box is refused here, not after a paid call.
 *
 * Pure model file: no `@angular/*`, no `leaflet`, no `this` (rule R1) — see
 * `client/src/app/components/leaflet-newcollections/ais/NEW-AIS-WIZARD.md`
 * Part 5.
 */

export interface ParsedIdentifiers {
  mmsi: string[];
  imo: string[];
  /** What was typed that is neither a 9-digit MMSI nor a 7-digit IMO. */
  rejected: string[];
  /** Combined count exceeds `cap` — never a silent truncation. */
  overCap: boolean;
}

const MMSI_RE = /^\d{9}$/;
const IMO_RE = /^\d{7}$/;

/**
 * Splits a raw, comma/space/newline-separated string of identifiers into
 * MMSI (9 digits) and IMO (7 digits) buckets, de-duplicated within each.
 * Anything else — a name, a partial number, a call sign — is `rejected`.
 *
 * `cap` comes from the sensor/operator document (`vesselFinderIdentifierCap`),
 * never a literal, so the client and the server's cap cannot drift.
 */
export function parseVesselIdentifiers(raw: string, cap: number): ParsedIdentifiers {
  const tokens = String(raw ?? '')
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  const mmsi = new Set<string>();
  const imo = new Set<string>();
  const rejected: string[] = [];

  for (const token of tokens) {
    if (MMSI_RE.test(token)) mmsi.add(token);
    else if (IMO_RE.test(token)) imo.add(token);
    else rejected.push(token);
  }

  const combined = mmsi.size + imo.size;
  const capNum = Number.isFinite(cap) && cap > 0 ? cap : Infinity;

  return {
    mmsi: [...mmsi],
    imo: [...imo],
    rejected,
    overCap: combined > capNum,
  };
}

/** True for a single valid MMSI (9 digits) — used by the per-vessel Master/Voyage/Port-calls controls. */
export function isValidMmsi(value: string | null | undefined): boolean {
  return MMSI_RE.test(String(value ?? '').trim());
}

/** True for a single valid IMO (7 digits). */
export function isValidImo(value: string | null | undefined): boolean {
  return IMO_RE.test(String(value ?? '').trim());
}
